'use client';

import React from 'react';

const layers = [
  {
    label: 'Frontend',
    color: '#A78BFA',
    items: ['Next.js 16 (App Router)', 'React Server Components', 'Tailwind + shadcn/ui'],
  },
  {
    label: 'API',
    color: '#EC4899',
    items: ['tRPC (type-safe contracts)', 'REST fallback'],
  },
  {
    label: 'Agent Orchestration',
    color: '#7C3AED',
    items: ['Temporal (workflows)', 'Inngest (events)', 'LangGraph + Vercel AI SDK', 'MCP server'],
  },
  {
    label: 'Intelligence Layer',
    color: '#22C55E',
    items: [
      'LLMs: Claude (primary), GPT-4, Gemini',
      'ML Serving: Modal or SageMaker',
      'Vector DB: pgvector / Pinecone / Weaviate',
    ],
  },
  {
    label: 'Data & Systems',
    color: '#F59E0B',
    items: [
      'Postgres + Prisma (pgvector-enabled)',
      'IoT: MQTT / AWS IoT',
      'OCR: AWS Textract / Claude Vision',
      'Observability: Langfuse / Braintrust',
    ],
  },
  {
    label: 'Integrations',
    color: '#06B6D4',
    items: [
      'Auth: Clerk / Auth.js',
      'Payments: Stripe + Plaid',
      'E-sign: DocuSign API',
      'Comms: Twilio · SendGrid · WhatsApp Business',
      'Infra: Vercel + AWS/Fly',
    ],
  },
];

const principles = [
  'RAG before fine-tuning',
  'Evals from day one',
  'Structured outputs only',
  'Human-in-the-loop by default',
  'Log every agent decision',
  'Start narrow, expand gradually',
];

export default function AITechStack() {
  return (
    <section className="py-24 px-6 relative">
      <div className="max-w-6xl mx-auto">
        <div className="reveal-blur mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[rgba(124,58,237,0.25)] mb-5">
            <span className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase">
              Technical Stack
            </span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}
          >
            A stack designed for{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              agents, not apps.
            </span>
          </h2>
        </div>

        {/* Stack layers */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children mb-10">
          {layers.map((l) => (
            <div key={l.label} className="reveal-blur bento-card card-shine p-5">
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: l.color, boxShadow: `0 0 10px ${l.color}` }}
                />
                <div
                  className="text-[10px] font-mono tracking-widest uppercase"
                  style={{ color: l.color }}
                >
                  {l.label}
                </div>
              </div>
              <ul className="flex flex-col gap-2">
                {l.items.map((it) => (
                  <li key={it} className="text-xs font-mono text-[#9999A8] leading-relaxed flex items-start gap-2">
                    <span className="mt-1 w-0.5 h-0.5 rounded-full bg-[#5A5A6E] shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* AI dev principles */}
        <div className="reveal-blur bento-card card-shine p-7">
          <div className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase mb-4">
            Six AI Development Principles
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {principles.map((p, i) => (
              <div
                key={p}
                className="flex items-start gap-3 p-3 rounded-lg border border-[rgba(255,255,255,0.04)] bg-[rgba(124,58,237,0.04)]"
              >
                <span className="font-mono text-[10px] text-[#A78BFA] shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-sm text-[#EEEEF0] font-manrope font-medium leading-snug">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
